import _get from 'lodash/get'
import {
  ERROR_EMAIL_INVALID,
  postBrooklyn as postEmailCapture,
} from 'services/email-signup'
import { TYPES } from 'components/Button'
import { getPrimary } from 'services/languages'
import { List } from 'immutable'
import { USD } from 'services/currency'
import { US } from 'services/country'
import {
  ADYEN_PAYMENT_TYPE_CARD,
  ADYEN_PAYMENT_TYPE_GOOGLEPAY,
  ADYEN_PAYMENT_TYPE_PAYPAL,
  adyenMerchantAccount,
  allowedPaymentMethods,
  getAdyenPaymentMethods,
} from 'services/adyen'
import { get as getConfig } from 'config'
import { TYPE_CHECKOUT_UPDATE_EMAIL, TYPE_LOGIN } from 'services/dialog'
import { URL_TERMS_PRIVACY } from 'services/url/constants'
import _size from 'lodash/size'
import { stringify as stringifyQuery } from 'services/query-string'
import { CHECKOUT_EDIT_CLICKED_GA4_EVENT } from 'services/event-tracking'

const config = getConfig()
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])

export const BUTTON_STATUS = {
  CREATE_ACCOUNT: 'CREATE_ACCOUNT',
  CREATING_ACCOUNT: 'CREATING_ACCOUNT',
  ACCOUNT_PROCESSING: 'ACCOUNT_PROCESSING',
  ACCOUNT_CREATED: 'ACCOUNT_CREATED',
  ACCOUNT_MODIFIED: 'ACCOUNT_MODIFIED',
  ACCOUNT_SAVED: 'ACCOUNT_SAVED',
}

const ORDERED_PAYMENT_METHOD_TYPES = [
  ADYEN_PAYMENT_TYPE_GOOGLEPAY,
  ADYEN_PAYMENT_TYPE_CARD,
  ADYEN_PAYMENT_TYPE_PAYPAL,
]

const SCROLL_DELAY = 500

export function validateEmailFormatOnBlur ({
  email,
  getUserEmailAvailability,
}) {
  const emailPattern = /^.+@.+\..{2,}$/
  if (!emailPattern.test(email)) {
    return
  }

  // check email availability
  getUserEmailAvailability(email)
}

/**
 * Handles the change of the email value and triggers related processes
 */
export function handleEmailChange ({
  props,
  value,
  emailValue,
  setEmailValue,
}) {
  const {
    user,
    checkout,
    resetUserEmailAvailability,
    resetUserChildAccount,
    resetCheckoutEmailCaptureStatus,
  } = props

  // Update the email value
  setEmailValue(value)

  // Solves the browser autocomplete issue when checking email
  if (!emailValue && value) {
    validateEmailFormatOnBlur(value, resetUserEmailAvailability)
  }

  // Resets email availability if necessary
  if (user.has('emailAvailable')) {
    resetUserEmailAvailability()
  }

  // Resets child account if necessary
  if (user.has('childProfile')) {
    resetUserChildAccount()
  }

  // Resets the error state if necessary
  if (checkout.has('emailCaptureErrorCode')) {
    resetCheckoutEmailCaptureStatus()
  }
}

/**
 * Handles the change of the terms and conditions checkbox value
 */
export function handleTermsChange ({
  checked,
  setTermsValue,
  setTermsInitialChecked,
  termsInitialChecked,
  setTermsError,
}) {
  setTermsValue(checked)

  if (checked) setTermsError(false)
  if (!termsInitialChecked) {
    setTermsInitialChecked(true)
  }
}

/**
 * Handles the form validation and submission process
 */
export async function handleFormSubmitValidation ({
  data,
  props,
  setShouldShowLoginModal,
  isEmailUnavailable,
}) {
  const { setCheckoutAccountCreateData } = props

  // the form has an error on the email and can't submit, show the login modal
  if (isEmailUnavailable) {
    setShouldShowLoginModal(true)
    return
  }
  await setCheckoutAccountCreateData(data)
}

/**
 * Updates the user data and triggers the user update request if there are changes
 * to email or password.
 */
export async function handleUserUpdate ({
  data,
  props,
  setUpdateError = () => {},
}) {
  const { email } = data
  const {
    auth,
    user,
    updateUser,
    getUserEmailAvailability,
  } = props

  // get email Availibility
  const emailAvailability = await getUserEmailAvailability(email)
  const oldEmail = user.getIn(['updateUser', 'email']) || user.getIn(['data', 'mail'])
  const isEmailAvailable = !!user.get('emailAvailable') || emailAvailability.available
  const canUpdateEmail = email && oldEmail !== email && isEmailAvailable

  if (canUpdateEmail) {
    try {
      // reset errors
      setUpdateError(false)

      // update user (this function refresh the JWT)
      const updateResult = await updateUser({
        auth,
        ...(canUpdateEmail && { email }),
      })

      // if successful, update the email preferences and states
      if (updateResult.success) {
        postEmailCapture({
          email: oldEmail,
          optin: false,
          fields: {},
        })
      }
    } catch (e) {
      setUpdateError(true)
    }
  }
}

/**
 * Validates the email availability and returns the appropriate error message
 * @returns {string|null} The error message if validation fails, otherwise null
 */
export function validateEmailAvailability ({
  props,
  email = '',
  isChildAccount,
  shouldShowLoginModal = false,
  setShouldShowLoginModal,
}) {
  const {
    user,
    staticText,
    checkout,
    setCheckoutLoginData,
    setOverlayDialogVisible,
  } = props
  let errorMessage = null

  // If email is unavailable and it's not a child account
  if (user.get('emailAvailable') === false && !isChildAccount) {
    // Show the login modal and update login modal flag state
    if (shouldShowLoginModal) {
      setCheckoutLoginData(email)
      setOverlayDialogVisible(TYPE_LOGIN)
      setShouldShowLoginModal(false)
    }
    errorMessage = staticText.getIn(['data', 'cantUseEmail'])
  } else if (checkout.get('emailCaptureErrorCode') === ERROR_EMAIL_INVALID) {
    errorMessage = staticText.getIn(['data', 'errorMessageInvalidEmail'])
  }

  return errorMessage
}

/**
 * Retrieves the button settings based on the current form status
 * @returns {Object} The settings for the form's button UI (text, type, etc.)
 */
export function getButtonSettings (buttonStatus, staticText) {
  const baseClass = 'cart-single-page__create-account-form-submit'
  let buttonSettings = {}
  switch (buttonStatus) {
    case BUTTON_STATUS.ACCOUNT_CREATED:
      buttonSettings = {
        buttonCopy: staticText.getIn(['data', 'accountCreated']),
        buttonType: TYPES.GHOST,
        buttonClassName: `${baseClass} ${baseClass}--check`,
        buttonIcon: true,
        buttonDisabled: true,
      }
      break
    case BUTTON_STATUS.CREATING_ACCOUNT:
      buttonSettings = {
        buttonCopy: staticText.getIn(['data', 'creatingAccount']),
        buttonType: TYPES.DEFAULT,
        buttonClassName: `${baseClass} ${baseClass}--disabled`,
        buttonSherpa: true,
        buttonDisabled: true,
      }
      break
    case BUTTON_STATUS.ACCOUNT_PROCESSING:
      buttonSettings = {
        buttonCopy: staticText.getIn(['data', 'processing']),
        buttonType: TYPES.DEFAULT,
        buttonClassName: `${baseClass} ${baseClass}--disabled`,
        buttonSherpa: true,
        buttonDisabled: true,
      }
      break
    case BUTTON_STATUS.ACCOUNT_MODIFIED:
      buttonSettings = {
        buttonCopy: staticText.getIn(['data', 'saveChanges']),
        buttonType: TYPES.PRIMARY,
        buttonClassName: baseClass,
        buttonDisabled: false,
      }
      break
    case BUTTON_STATUS.ACCOUNT_SAVED:
      buttonSettings = {
        buttonCopy: staticText.getIn(['data', 'changesSaved']),
        buttonType: TYPES.GHOST,
        buttonClassName: `${baseClass} ${baseClass}--check`,
        buttonIcon: true,
        buttonDisabled: true,
      }
      break
    case BUTTON_STATUS.CREATE_ACCOUNT:
    default:
      buttonSettings = {
        buttonCopy: staticText.getIn(['data', 'createAccount']),
        buttonType: TYPES.PRIMARY,
        buttonClassName: baseClass,
        buttonDisabled: false,
      }
      break
  }
  return buttonSettings
}

/**
 * Returns a forced error message if the email is unavailable.
 * @param {boolean} emailUnavailable - Indicates if the email is unavailable
 * @returns {string} The error code
 */
export function getForcedEmailError (emailUnavailable) {
  let forcedError = ''
  if (emailUnavailable) {
    forcedError = 'isEmailUnavailable'
  }

  return forcedError
}

/**
 * Handles the invalid form submission by setting errors for the form and terms
 */
export function onInvalidFormSubmit ({
  data,
  isGdprCountry,
  setFormError,
  setTermsError,
}) {
  const {
    firstName,
    email,
    terms,
    password,
  } = data
  const hasTermsError = isGdprCountry && !terms

  if (hasTermsError
    || !firstName
    || !email
    || !password
  ) {
    setFormError(true)
  }

  if (hasTermsError) setTermsError(true)
}

/**
 * Get available payment methods for the user
 * Note: authentication is required for the post request to Adyen
 */
export async function getPaymentMethods ({
  auth,
  user,
  plans,
  userAccount,
}) {
  try {
    const plansList = planGraphQLActive ? plans || List() : plans.getIn(['data', 'plans'], List()) || List()
    const userRequestCountry = user.getIn(['data', 'requestCountry'])
    const plansCurrency = plansList.size > 0 ? plansList.first().get('currencyIso') : undefined
    const plansCountry = plansList.size > 0 ? plansList.first().get('country') : undefined
    const authCountry = auth.get('country')
    const userAccountCountry = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'countryIso'])
    const userAccountCurrency = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'currencyIso'])
    const country = userAccountCountry || plansCountry || authCountry || userRequestCountry || US
    const currencyIso = userAccountCurrency || plansCurrency || USD
    const userUuid = user.getIn(['data', 'uuid'])
    const userLanguage = getPrimary(user.getIn(['data', 'language'], List()))
    // get available payment methods
    const response = await getAdyenPaymentMethods({
      auth,
      shopperReference: userUuid,
      merchantAccount: adyenMerchantAccount,
      countryCode: country,
      shopperLocale: userLanguage,
      allowedPaymentMethods,
      amount: { currency: currencyIso, value: 0 },
    })
    // verify the response and return the payment methods
    if (response && response.success && response.data.paymentMethods) {
      const { paymentMethods } = response.data
      return ORDERED_PAYMENT_METHOD_TYPES.map((type) => {
        return paymentMethods.find((method) => method.type === type)
      })
    }
    // return an empty array if the response is invalid
    return []
  } catch (e) {
    return []
  }
}

/**
 * Scrolls to a section in the page
 */
export function scrollToSection (ref) {
  const target = ref.current
  if (!target) return

  const targetPosition = target.offsetTop
  const startPosition = window.scrollY
  const distance = targetPosition - startPosition
  const duration = distance / 2.5
  let startTime = null
  const startAnimationTime = Date.now() + SCROLL_DELAY

  function animateScroll (timestamp) {
    if (Date.now() < startAnimationTime) {
      requestAnimationFrame(animateScroll)
      return
    }

    if (!startTime) startTime = timestamp
    const progress = timestamp - startTime
    const easedProgress = Math.min(progress / duration, 1)
    const scroll = startPosition + (distance * easedProgress)
    window.scrollTo(0, scroll)

    if (progress < duration) {
      requestAnimationFrame(animateScroll)
    } else {
      window.scrollTo({
        top: targetPosition,
        left: 0,
        behavior: 'smooth',
      })
    }
  }

  requestAnimationFrame(animateScroll)
}

/**
 * Focuses on the email input and scrolls to the section
 */
export function onEditMail (props) {
  const {
    setDialogOptions,
    setDefaultGa4Event,
    setOverlayDialogVisible,
    resetUserEmailAvailability,
  } = props

  // reset email availability
  resetUserEmailAvailability()

  // open modal
  setDialogOptions(
    null,
    false,
    'modal--checkout-update-email',
  )
  setOverlayDialogVisible(TYPE_CHECKOUT_UPDATE_EMAIL)

  // fire the GA event
  setDefaultGa4Event(CHECKOUT_EDIT_CLICKED_GA4_EVENT)
}

/**
 * Get the privacy policy URL
 */
export function getPrivacyUrl (location = {}) {
  const { query = {} } = location
  const privacyUrl = `${URL_TERMS_PRIVACY}${_size(query) > 0 ? `?${stringifyQuery(query)}` : ''}`

  return privacyUrl
}
